import { Component, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { PronunciationResult } from '@app/model/pronunciation-result';
import { SpeechaceApiService } from '@app/services/api/speechace-api.service';
import { ChapterListeningService } from '@app/services/chapter-listening.service';
import { TranslateService } from '@ngx-translate/core';
import { QuestionType } from 'library-explorer';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, delay, finalize, map, mergeMap, retryWhen, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AudioRecordComponent } from '../audio-record/audio-record.component';

@Component({
    selector: 'app-pronunciation-question',
    templateUrl: './pronunciation-question.component.html',
    styleUrls: ['./pronunciation-question.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PronunciationQuestionComponent),
            multi: true
        }
    ],
    standalone: false
})
export class PronunciationQuestionComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @ViewChild(AudioRecordComponent) public audioRecord: AudioRecordComponent;
  @Input() public questionHash: string;
  @Input() public id: string;
  @Input() public text: string;

  public isLoading = false;

  public words: { word: string, score: number }[] = [];

  public readonly PASS_RATE_SCORE = 60;

  private subscriptions: Subject<void> = new Subject();

  constructor(
    private readonly toastrService: ToastrService,
    private readonly translateService: TranslateService,
    private readonly chapterListeningService: ChapterListeningService,
    private readonly toastr: ToastrService,
    private readonly speechaceApiService: SpeechaceApiService) { }

  ngOnInit(): void {
    this.words = this.text?.split(' ').map(word => ({ word, score: -1 }));
  }

  ngOnDestroy(): void {
    this.stopSpeech();
    this.subscriptions.next();
    this.subscriptions.unsubscribe();
  }

  public control: FormControl;
  public speech: HTMLAudioElement;
  public isSpeechLoading = false;

  private loadedSpeechUrl: string;

  onChange = (data: any) => { };
  onTouched = () => { };

  public toggleListeningMode(): void {
    if (this.speech) {
      this.isSpeechLoading = false;
      this.stopSpeech();
      return;
    }

    this.startSpeech();
  }

  public stopSpeech(): void {
    if (!this.speech) {
      return;
    }

    this.speech.pause();
    this.speech = null;
  }

  public startSpeech(): void {
    this.fetchSpeech().subscribe(url => {
      this.speech = new Audio(url);

      this.speech.onended = () => {
        this.speech = null;
      }

      this.speech.play();
    });
  }

  public writeValue(obj: any): void {
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public fileChanged(data: File): void {
    if (!data) {
      return;
    }
  
    this.isLoading = true;

    this.speechaceApiService.checkSlidePronunciation(this.id, data)
      .pipe(
        finalize(() => this.isLoading = false),
        catchError(err => {
          this.audioRecord.resetPlayer();
          throw err;
        })
      )
      .subscribe(data => {
        this.words = data.wordScoreList;
        this.emitAnswer(data);
      });
  }

  public emitAnswer(data: PronunciationResult): void {
    this.onChange({
      questionHash: this.questionHash,
      type: QuestionType.QUESTION_PRONUNCIATION,
      isCorrect: data.totalScore >= this.PASS_RATE_SCORE
    });
  }

  private fetchSpeech(): Observable<string> {
    if (this.loadedSpeechUrl) {
      return of(this.loadedSpeechUrl);
    }

    this.isSpeechLoading = true;

    return this.chapterListeningService.fetchParagraphSpeech({id: this.id} as any).pipe(
      mergeMap(([{url}]) => {
        return this.chapterListeningService.pollFile(url)
          .pipe(
            map(() => url)
          );
      }),
      tap(url => this.loadedSpeechUrl = url),
      finalize(() => this.isSpeechLoading = false),
      retryWhen(errors => errors.pipe(
        switchMap((err) => {
          if (err.status === 403) {
            return of(err).pipe(delay(1000));
          }

          return throwError(err);
        }),
        take(2),
      )),
      takeUntil(this.subscriptions)
    );
  }


}
